import { Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

import { BasemapConfigurationArrayConverter, CreateBasemapsFromConfig, IBasemapConfig } from './basemap-config';

let Basemap: __esri.BasemapConstructor;
let BasemapGallery: __esri.BasemapGalleryConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-basemap-switcher',
    styleUrls: ['../../sharedStyles/base.scss'],
    templateUrl: './basemap-switcher.component.html'
})
export class BasemapSwitcherComponent extends BaseComponent {
    @ViewChild('basemapSwitcherArea') private readonly basemapSwitcherAreaRef: ElementRef<HTMLDivElement>;

    @Input()
    @InputConverter(BasemapConfigurationArrayConverter)
    basemapConfigs: IBasemapConfig[] = [];

    @Input() basemaps: string = '';
    @Input() basemapGallery: NonNullable<__esri.BasemapGallery>;
    @Input() esriBasemaps: NonNullable<__esri.Basemap[]> = [];
    @Input() mapViewRef: NonNullable<__esri.MapView>;

    bingSupplementaryIds = ['BingMapsRoad', 'BingMapsAerial', 'BingMapsHybrid'];
    @Input() possibleSupplementaryIds = [...this.bingSupplementaryIds];

    @Input()
    public addBasemaps(basemapIds: string | string[], basemapConfigs: IBasemapConfig[]) {
        if (Array.isArray(basemapIds)) {
            basemapIds = basemapIds.join(',');
        }
        this.basemaps = basemapIds;
        this.basemapConfigs = basemapConfigs;
        //shouldSetup will fire the setter function but does not block
        //this call from resolving before loadBasemapGallery is called.
        //A future implementation may want to consider this to ensure data
        //integrity at time of completing this function
        this.shouldSetup = true;
    }

    @Input()
    get shouldSetup() { return this._shouldSetup; }
    set shouldSetup(value) {
        this._shouldSetup = value;
        if (value) {
            //on setup perform a full load of the basemap gallery as both setupBasemaps
            //and createBasemapGallery must be called
            this.loadBasemapGallery();
        }
    }
    private _shouldSetup = false;

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService) {
        //do not indicate ready until the first initialization of the gallery
        //has been completed to load the values of this basemap from current
        //properties
        super(_vcr, false);
        this.finishSetup();
    }

    private async finishSetup() {
        await this.loadBasemapGallery();
        this.isReady();
    }

    private async loadBasemapGallery() {
        //filter out supplementary IDs out of the basemap IDs so they do not get passed down to ESRI
        await this.setMapViewRef();
        await this.setupBasemaps();
        await this.createSupplementaryBasemaps();
        await this.createBasemapGallery();
    }

    private async setMapViewRef() {
        if (!this.mapViewRef) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.mapViewRef = parent.mapView;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }
    }

    private async setupBasemaps() {
        this.esriBasemaps = [];
        await this.createBasemapsFromConfig();
        await this.createBasemapsFromIds();
        this.shouldSetup = false;
    }

    private async createBasemapsFromConfig(): Promise<any> {
        for (const item of this.basemapConfigs) {
            if (!this.basemapExists(item.title)) {
                await this.layerService.getLayerCopyRight(item);
                this.esriBasemaps.push(await CreateBasemapsFromConfig(item));
            }
        }
    }

    private async createBasemapsFromIds(): Promise<any> {
        if (!Basemap) {
            [Basemap] = await Promise.all([ESRI.Basemap]);
        }
        let basemapArr = this.basemaps.split(',').map(x => x.trim());
        basemapArr = basemapArr.filter((item) => !this.possibleSupplementaryIds.includes(item));
        for (const item of basemapArr) {
            if (!this.basemapExists(item)) {
                this.esriBasemaps.push(Basemap.fromId(item));
            }
        }
    }

    /*
    * Additional unique identifiers to add an abstract set of possible basemaps. Currently this
    * is used to implement Bing Road, Imagery, and Hybrid maps in case the using program requires
    * this maps
    */
    private async createSupplementaryBasemaps() {

        let i = 0;
        const basemapArr = this.basemaps.split(',').map(x => x.trim());
        const supplementaryIds = basemapArr.filter((item) => this.possibleSupplementaryIds.includes(item));
        for (const supp of supplementaryIds) {
            let BingMapLayer: __esri.BingMapsLayerConstructor;
            [Basemap, BingMapLayer] = await Promise.all([ESRI.Basemap, ESRI.BingMapsLayer]);
            //handle bing specific supplementary basemaps
            if (this.bingSupplementaryIds.includes(supp)) {
                //determine the proper style and title depending on the specific BingMap
                const mapProps = {
                    style: undefined,
                    title: '',
                    thumbnailUrl: '',
                    id: `${supp}${i}`
                };
                switch (supp) {
                    case 'BingMapsRoad':
                        mapProps.style = 'road';
                        mapProps.title = 'Bing Streets';
                        mapProps.thumbnailUrl = environment.bingBasemaps.thumbnails.street;
                        break;
                    case 'BingMapsAerial':
                        mapProps.style = 'aerial';
                        mapProps.title = 'Bing Imagery';
                        mapProps.thumbnailUrl = environment.bingBasemaps.thumbnails.imagery;
                        break;
                    case 'BingMapsHybrid':
                        mapProps.style = 'hybrid';
                        mapProps.title = 'Bing Imagery w/ Labels';
                        mapProps.thumbnailUrl = environment.bingBasemaps.thumbnails.hybrid;
                        break;
                    default:
                        break;
                }

                const opacityModifier = 0.000001;
                this.esriBasemaps.push(
                    new Basemap({
                        baseLayers: [
                            new BingMapLayer({
                                ...mapProps,
                                key: environment.bingBasemaps.key,
                                opacity: 1 - opacityModifier * i //this setting of opacity is a work around for ESRI comparisons that just take into consideration type, url, min scale, max scale, visibility, and opacity. In the
                                //case of BingMaps, URL and Type are always equivalent ('bing-maps' and 'null') and the others are not set by default. Overriding the opacity to make sure that each map is considered
                                //unique by this comparison. Found in dojo.js formatted line dojo.js formatted line 22302
                            })
                        ],
                        ...mapProps
                    })
                );
            }
            i++;
        }
    }

    private async createBasemapGallery(): Promise<any> {
        //when the basemapGallery already has been initialized, remove all the
        //basemaps from it, add the set we currently have, refresh to force the
        //Gallery to reload from the source array and force a new render of the gallery
        //this allows for an inplace update of the existing gallery with no deconstruction
        //and is needed since addBasemaps will add additional basemaps after initialization
        //of the gallery has been completed.

        if (this.basemapGallery) {
            this.basemapGallery.source.basemaps.removeAll();
            this.basemapGallery.source.basemaps.addMany(this.esriBasemaps);
            this.basemapGallery.source.refresh();
            this.basemapGallery.renderNow();
            return;
        }
        [BasemapGallery] = await Promise.all([ESRI.BasemapGallery]);
        this.basemapGallery = new BasemapGallery({
            container: this.basemapSwitcherAreaRef.nativeElement,
            source: <any>this.esriBasemaps,
            view: this.mapViewRef
        });
    }

    private basemapExists(id: string) {
        return this.esriBasemaps.find(x => x.id === id);
    }
}
